// Form Fill store — plan §4.4.1 + §6.1.
// Local yanıt state + debounce flush (800ms) + 8-state save matrix + offline queue.
//
// State matrix (§6.1):
//   idle     : hiç değişiklik yok
//   typing   : kullanıcı yazıyor, debounce bekliyor
//   saving   : PUT in-flight
//   saved    : PUT success (2s sonra idle)
//   retry    : ağ/5xx hatası + exp-backoff (1s/3s/8s/20s, max 4 deneme) — YALNIZ zamanlayıcı kuruluyken
//   offline  : max retry hit veya navigator.onLine=false → queue localStorage'da
//   syncing  : online geri geldi, queue flush
//   conflict : server STALE_WRITE → modal
//   invalid  : yalnız sunucunun reddettiği (düzeltilmesi gereken) öğeler kaldı (UX P1 K4)
//   failed   : kalıcı istemci hatası (4xx, 409/429 hariç) — otomatik yeniden deneme yok (I1)
//
// Kayıt döngüsü (flush/backoff/kuyruk) ./form-fill-flush.ts'te.

import { create } from "zustand";
import { api, apiRaw } from "../api/client";
import {
  clearFlushTimers,
  clearQueue,
  flushPending,
  persistQueue,
  restoreQueue,
  scheduleFlush as scheduleFlushWith,
} from "./form-fill-flush";
import { getProjectProvenance } from "../api/report-import";
import type { ImportQuestionFilter } from "../lib/report-import-ui";
import { isAnswerComplete, type KpiFormMismatch } from "@mahir/shared";
import type {
  FormResponse,
  MissingFieldsResponse,
  PresenceUser,
  PriorYearEntry,
  PriorYearHintsResponse,
  ProjectProvenanceResponse,
  ProjectSnapshotResponse,
} from "@mahir/shared";

export type SaveState =
  | "idle"
  | "typing"
  | "saving"
  | "saved"
  | "retry"
  | "offline"
  | "syncing"
  | "conflict"
  | "invalid"
  | "failed";

export interface SupersededNotice {
  /** Kullanıcının yazdığı (atlanan) değer ve not. */
  mine: unknown;
  mineNote: string | null | undefined;
  /** Sunucudaki saklı istemci damgası — yeniden gönderim bunun üstünde damgalanır. */
  storedClientUpdatedAt: string | null;
}

export interface PendingChange {
  questionCode: string;
  value: unknown;
  consultantNote?: string | null;
  clientUpdatedAt: string;
}

// M9-S3: Undo/redo — soru bazında 50-adımlık ring buffer. Client-side only.
// Server-side form_response_history trigger zaten var (M4-S2); bu yalnız editör UX.
const UNDO_RING_SIZE = 50;

interface UndoRing {
  past: unknown[];
  future: unknown[];
}

export interface FormFillState {
  projectId: string | null;
  snapshot: ProjectSnapshotResponse | null;
  responsesByCode: Record<string, FormResponse>;
  pending: Record<string, PendingChange>;
  saveState: SaveState;
  lastSavedAt: string | null;
  retryAttempt: number; // 0-based; 4 ulaşınca offline
  conflictDetails: { questionCode: string; message: string; serverUpdatedAt?: string }[] | null;
  errorMessage: string | null;
  // M15 DR6 — soru + parça düzeyinde hata slotu. Global şerit (errorMessage) özet
  // olarak kalır; kart içi mesajlar buradan okunur. partKey yalnızca composite'te.
  errorsByCode: Record<string, { partKey?: string; message: string }[]>;
  /**
   * UX P1 R1 — sunucu yazımımızı atladı (aynı kullanıcının daha yeni değeri vardı) ve
   * sunucudaki değer gönderdiğimizden FARKLI. Kartta bildirim + "Benimkini uygula".
   */
  supersededByCode: Record<string, SupersededNotice>;
  activeSectionId: string | null;
  isOnline: boolean;
  // S4 (+M15 U4 — composite parça satırları)
  missingFields: {
    sectionCode: string;
    sectionLabel: string;
    questionCode: string;
    questionLabel: string;
    sectionId: string;
    partKey?: string | undefined;
    partLabel?: string | undefined;
  }[];
  priorYearByCode: Record<string, PriorYearEntry>;
  /** Önceki dönem önerileri fetch'i tamamlandı mı — "öneri yok" satırı yükleme
   *  sırasında yanıp sönmesin diye (Task 11 / 2D). Client modunda hiç true olmaz. */
  priorYearLoaded: boolean;
  /** M16 T21 — soru kodu → form ↔ KPI karşılaştırma satırı (projede KPI seti varsa; iç aktör). */
  kpiConsistencyByCode: Record<string, KpiFormMismatch>;
  presenceOthers: PresenceUser[];
  // M9-S3 undo/redo ring
  undoRings: Record<string, UndoRing>;

  // --- Geçmiş Rapor Aktarımı / köken (Task 15) ---
  /** `null` = hiç aktarım yok VEYA henüz yüklenmedi; hata AYRI bayrakta (8A). */
  provenance: ProjectProvenanceResponse | null;
  /** Köken fetch'i başarısız oldu — "null ≠ hata" (8A `provenanceUnavailable`). */
  provenanceError: boolean;
  /** Aktif çip (17A). Bölüm filtresi "all" dışında devre dışı kalır (2A düz liste). */
  provenanceFilter: ImportQuestionFilter;
  /** 11A — oturum içi açık kabul ("bu değeri kullan"). Kalıcı kapanış dolu yanıttan türetilir. */
  resolvedRejections: Set<string>;
  /** 2A — düzenlenen soru blur'a kadar filtrelenmiş listede kalır. */
  pinnedCode: string | null;

  load: (projectId: string, opts?: { isClient?: boolean }) => Promise<void>;
  reloadProvenance: () => Promise<void>;
  setProvenanceFilter: (filter: ImportQuestionFilter) => void;
  markRejectionResolved: (questionCode: string) => void;
  setPinnedCode: (questionCode: string | null) => void;
  /**
   * M15 D6 — updater fonksiyonu desteklenir: `setValue(code, (prev: unknown) => ({...}))`.
   * (Tip imzası `unknown` — fonksiyon değeri runtime'da updater sayılır; form
   * değerleri JSON olduğundan gerçek değer asla fonksiyon olamaz.)
   * Composite parça yazımları HEP updater ile yapılmalı — AttachmentUploader'ın
   * 2.5sn poll'ü gibi gecikmeli onChange'lerde bayat closure diğer parçaları
   * silerdi; updater store-yazım anındaki güncel değeri okur.
   */
  setValue: (questionCode: string, value: unknown) => void;
  setNote: (questionCode: string, note: string) => void;
  flushNow: () => Promise<void>;
  setActiveSection: (sectionId: string) => void;
  dismissConflict: () => Promise<void>;
  handleOnlineEvent: () => void;
  handleOfflineEvent: () => void;
  refreshMissing: () => Promise<void>;
  setPresenceOthers: (others: PresenceUser[]) => void;
  undoValue: (questionCode: string) => void;
  redoValue: (questionCode: string) => void;
  canUndo: (questionCode: string) => boolean;
  canRedo: (questionCode: string) => boolean;
  /** R1 — atlanan değeri taze (saklı damganın üstünde) damgayla yeniden gönderir. */
  applySupersededMine: (questionCode: string) => void;
  /** R1 — sunucudaki değeri kabul et, bildirimi kapat. */
  dismissSuperseded: (questionCode: string) => void;
}

let loadSeq = 0; // yalnız SON load() yanıtı yazar (StrictMode çift mount / hızlı proje geçişi)
export const useFormFillStore = create<FormFillState>((set, get) => ({
  projectId: null,
  snapshot: null,
  responsesByCode: {},
  pending: {},
  saveState: "idle",
  lastSavedAt: null,
  retryAttempt: 0,
  conflictDetails: null,
  errorMessage: null,
  errorsByCode: {},
  supersededByCode: {},
  activeSectionId: null,
  isOnline: typeof navigator !== "undefined" ? navigator.onLine : true,
  missingFields: [],
  priorYearByCode: {},
  priorYearLoaded: false,
  kpiConsistencyByCode: {},
  presenceOthers: [],
  undoRings: {},
  provenance: null,
  provenanceError: false,
  provenanceFilter: "all",
  resolvedRejections: new Set<string>(),
  pinnedCode: null,

  load: async (projectId, opts) => {
    // eng-review D11 — client aktöre /prior-year whitelist'te YOK (auth/client-gate.ts).
    // Koşulsuz çağrılırsa CLIENT_FORBIDDEN döner; zararsız olsa da (kickout tetiklemez)
    // gereksiz 403 gürültüsü — client modunda hiç çağrılmaz, PriorYearHint zaten
    // client'ta render edilmiyor (FormFill.tsx).
    const isClient = opts?.isClient === true;
    const seq = ++loadSeq;
    // Offline queue'da bekleyen varsa restore et
    const restored = restoreQueue(projectId);

    set({
      projectId,
      snapshot: null,
      responsesByCode: {},
      pending: restored,
      saveState: "idle",
      lastSavedAt: null,
      retryAttempt: 0,
      conflictDetails: null,
      errorMessage: null,
      errorsByCode: {},
      supersededByCode: {},
      priorYearByCode: {},
      priorYearLoaded: false,
      kpiConsistencyByCode: {},
      // B11 — köken state'i proje değişiminde sıfırlanır; aksi halde önceki projenin
      // rozetleri/çip filtresi yeni projeye sızar.
      provenance: null,
      provenanceError: false,
      provenanceFilter: "all",
      resolvedRejections: new Set<string>(),
      pinnedCode: null,
    });

    // Köken (Task 15) — snapshot/responses ile PARALEL (await edilmez; snapshot
    // hatasında erken dönülse bile bandın hata satırı doğru gösterilir).
    // Client aktör whitelist dışı (GET /projects/:id/provenance client'a kapalı) ve
    // köken UI'sı client'ta hiç render edilmiyor → istek hiç atılmaz.
    if (!isClient) void get().reloadProvenance();

    const snapRes = await apiRaw<ProjectSnapshotResponse>(`/projects/${projectId}/snapshot`);
    if (!snapRes.ok) {
      set({ saveState: "offline", errorMessage: "Proje snapshot'ı yüklenemedi" });
      return;
    }
    const respRes = await apiRaw<{ responses: FormResponse[] }>(`/projects/${projectId}/responses`);
    if (seq !== loadSeq) return; // bayat: sonraki load kullanıcının bölüm seçimini ezmesin
    if (!respRes.ok) {
      set({ saveState: "offline", errorMessage: "Yanıtlar yüklenemedi" });
      return;
    }

    const byCode: Record<string, FormResponse> = {};
    for (const r of respRes.body.responses) byCode[r.questionCode] = r;

    const firstSectionId = snapRes.body.snapshot.sections
      .slice()
      .sort((a, b) => a.order - b.order)[0]?.id;

    set({
      snapshot: snapRes.body,
      responsesByCode: byCode,
      activeSectionId: firstSectionId ?? null,
      saveState: Object.keys(restored).length > 0 ? "syncing" : "idle",
    });

    // Restore edildiyse hemen flush dene
    if (Object.keys(restored).length > 0) {
      scheduleFlush(get, 50);
    }

    // Missing fields + prior year — paralel fetch (client modunda prior-year atlanır)
    void (async () => {
      try {
        const [mRes, pRes, kRes] = await Promise.all([
          apiRaw<MissingFieldsResponse>(`/projects/${projectId}/missing`),
          isClient
            ? Promise.resolve({
                ok: true,
                status: 200,
                body: { hints: [] as PriorYearEntry[] },
              })
            : apiRaw<PriorYearHintsResponse>(`/projects/${projectId}/prior-year`),
          // M16 T21 — form ↔ KPI tutarlılığı (client'a kapalı; hata non-fatal)
          isClient
            ? Promise.resolve({
                ok: true,
                status: 200,
                body: { hasKpiSet: false, mismatches: [] as KpiFormMismatch[] },
              })
            : apiRaw<{ hasKpiSet: boolean; mismatches: KpiFormMismatch[] }>(
                `/kpi/projects/${projectId}/kpi-consistency`,
              ),
        ]);
        const priorByCode: Record<string, PriorYearEntry> = {};
        if (pRes.ok) for (const h of pRes.body.hints) priorByCode[h.questionCode] = h;
        const kpiByCode: Record<string, KpiFormMismatch> = {};
        if (kRes.ok) for (const m of kRes.body.mismatches) kpiByCode[m.questionCode] = m;
        set({
          missingFields: mRes.ok ? mRes.body.missing : [],
          priorYearByCode: priorByCode,
          kpiConsistencyByCode: kpiByCode,
          // Client modunda istek hiç atılmaz → "öneri yok" satırı da çıkmaz.
          priorYearLoaded: !isClient && pRes.ok,
        });
      } catch {
        // non-fatal
      }
    })();
  },

  reloadProvenance: async () => {
    const projectId = get().projectId;
    if (!projectId) return;
    try {
      const res = await getProjectProvenance(projectId);
      // Araya başka bir proje yüklendiyse bayat cevabı yazma.
      if (get().projectId !== projectId) return;
      set({ provenance: res, provenanceError: false });
    } catch {
      if (get().projectId !== projectId) return;
      // 8A — null ≠ hata: bant "yüklenemedi + tekrar dene" satırına düşer.
      set({ provenance: null, provenanceError: true });
    }
  },

  setProvenanceFilter: (filter) => {
    // Çip değişiminde pin düşer — önceki filtrenin sabitlediği kart yeni listede kalmasın.
    set({ provenanceFilter: filter, pinnedCode: null });
  },

  markRejectionResolved: (questionCode) => {
    set((s) => {
      if (s.resolvedRejections.has(questionCode)) return {};
      const next = new Set(s.resolvedRejections);
      next.add(questionCode);
      return { resolvedRejections: next };
    });
  },

  setPinnedCode: (questionCode) => {
    set({ pinnedCode: questionCode });
  },

  setValue: (questionCode, value) => {
    set((s) => {
      // Undo ring'e mevcut değeri push — redo temizlenir (yeni dallanma).
      // Ş2 fix — prev, selectValue semantiğiyle AYNI: pending anahtarı VARSA onun
      // değeri (null olsa bile) esas alınır; ?? zinciri null pending'i sunucu
      // değerine düşürüp updater'a silinmiş veriyi diriltebiliyordu.
      const pendingEntry = s.pending[questionCode];
      const prev =
        pendingEntry !== undefined
          ? pendingEntry.value
          : (s.responsesByCode[questionCode]?.value ?? null);
      // M15 D6 — updater store-yazım anındaki güncel değeri okur (bayat closure imkansız)
      const nextValue =
        typeof value === "function" ? (value as (p: unknown) => unknown)(prev) : value;
      const ring = s.undoRings[questionCode] ?? { past: [], future: [] };
      // Aynı değeri iki kez itme (no-op update filter)
      const nextPast =
        ring.past[ring.past.length - 1] === prev
          ? ring.past
          : [...ring.past.slice(-(UNDO_RING_SIZE - 1)), prev];
      // Kullanıcı düzeltmeye başladı — bu sorunun kart içi hataları temizlenir
      // (bir sonraki flush ya başarır ya güncel hatayı yeniden yazar)
      const { [questionCode]: _cleared, ...remainingErrors } = s.errorsByCode;
      return {
        pending: {
          ...s.pending,
          [questionCode]: {
            questionCode,
            value: nextValue,
            consultantNote: s.pending[questionCode]?.consultantNote ?? null,
            clientUpdatedAt: new Date().toISOString(),
          },
        },
        saveState: s.saveState === "offline" ? "offline" : "typing",
        undoRings: {
          ...s.undoRings,
          [questionCode]: { past: nextPast, future: [] },
        },
        errorsByCode: remainingErrors,
        supersededByCode: withoutKey(s.supersededByCode, questionCode),
      };
    });
    persistQueue(get);
    scheduleFlush(get);
  },

  setNote: (questionCode, note) => {
    set((s) => ({
      pending: {
        ...s.pending,
        [questionCode]: {
          questionCode,
          value: s.pending[questionCode]?.value ?? s.responsesByCode[questionCode]?.value ?? null,
          consultantNote: note,
          clientUpdatedAt: new Date().toISOString(),
        },
      },
      saveState: s.saveState === "offline" ? "offline" : "typing",
    }));
    persistQueue(get);
    scheduleFlush(get);
  },

  flushNow: async () => {
    clearFlushTimers();
    await flushPending(get, setStore);
  },

  setActiveSection: (sectionId) => {
    set({ activeSectionId: sectionId });
  },

  dismissConflict: async () => {
    const projectId = get().projectId;
    if (!projectId) return;
    clearQueue(projectId);
    set({ conflictDetails: null, pending: {}, saveState: "idle", retryAttempt: 0 });
    await get().load(projectId);
  },

  handleOnlineEvent: () => {
    set({ isOnline: true });
    const state = get();
    if (state.saveState === "offline" && Object.keys(state.pending).length > 0) {
      set({ saveState: "syncing", retryAttempt: 0 });
      scheduleFlush(get, 50);
    }
  },

  handleOfflineEvent: () => {
    set({ isOnline: false });
  },

  refreshMissing: async () => {
    const projectId = get().projectId;
    if (!projectId) return;
    try {
      const res = await api<MissingFieldsResponse>(`/projects/${projectId}/missing`);
      set({ missingFields: res.missing });
    } catch {
      // non-fatal
    }
  },

  setPresenceOthers: (others) => {
    set({ presenceOthers: others });
  },

  undoValue: (questionCode) => {
    const s = get();
    const ring = s.undoRings[questionCode];
    if (!ring || ring.past.length === 0) return;
    const prev = ring.past[ring.past.length - 1];
    const remaining = ring.past.slice(0, -1);
    const current =
      s.pending[questionCode]?.value ?? s.responsesByCode[questionCode]?.value ?? null;
    set((st) => {
      // B4 fix — geri alınan değer için bayat kart-içi hata satırı kalmasın
      const { [questionCode]: _cleared, ...remainingErrors } = st.errorsByCode;
      return {
        pending: {
          ...st.pending,
          [questionCode]: {
            questionCode,
            value: prev,
            consultantNote: st.pending[questionCode]?.consultantNote ?? null,
            clientUpdatedAt: new Date().toISOString(),
          },
        },
        saveState: st.saveState === "offline" ? "offline" : ("typing" as const),
        undoRings: {
          ...st.undoRings,
          [questionCode]: {
            past: remaining,
            future: [...ring.future.slice(-(UNDO_RING_SIZE - 1)), current],
          },
        },
        errorsByCode: remainingErrors,
        supersededByCode: withoutKey(st.supersededByCode, questionCode),
      };
    });
    persistQueue(get);
    scheduleFlush(get);
  },

  redoValue: (questionCode) => {
    const s = get();
    const ring = s.undoRings[questionCode];
    if (!ring || ring.future.length === 0) return;
    const next = ring.future[ring.future.length - 1];
    const remaining = ring.future.slice(0, -1);
    const current =
      s.pending[questionCode]?.value ?? s.responsesByCode[questionCode]?.value ?? null;
    set((st) => {
      // B4 fix — yinelenen değer için bayat kart-içi hata satırı kalmasın
      const { [questionCode]: _cleared, ...remainingErrors } = st.errorsByCode;
      return {
        pending: {
          ...st.pending,
          [questionCode]: {
            questionCode,
            value: next,
            consultantNote: st.pending[questionCode]?.consultantNote ?? null,
            clientUpdatedAt: new Date().toISOString(),
          },
        },
        saveState: st.saveState === "offline" ? "offline" : ("typing" as const),
        undoRings: {
          ...st.undoRings,
          [questionCode]: {
            past: [...ring.past.slice(-(UNDO_RING_SIZE - 1)), current],
            future: remaining,
          },
        },
        errorsByCode: remainingErrors,
        supersededByCode: withoutKey(st.supersededByCode, questionCode),
      };
    });
    persistQueue(get);
    scheduleFlush(get);
  },

  canUndo: (questionCode) => (get().undoRings[questionCode]?.past.length ?? 0) > 0,
  canRedo: (questionCode) => (get().undoRings[questionCode]?.future.length ?? 0) > 0,

  applySupersededMine: (questionCode) => {
    const notice = get().supersededByCode[questionCode];
    if (!notice) return;
    get().setValue(questionCode, notice.mine); // bildirimi de temizler
    // Taze damga saklı istemci damgasının ÜSTÜNDE olmalı (saati geride cihazda yine atlanmasın).
    const stored = notice.storedClientUpdatedAt ? Date.parse(notice.storedClientUpdatedAt) : 0;
    set((s) => {
      const p = s.pending[questionCode];
      if (!p) return {};
      return {
        pending: {
          ...s.pending,
          [questionCode]: {
            ...p,
            consultantNote: notice.mineNote ?? p.consultantNote ?? null,
            clientUpdatedAt: new Date(Math.max(Date.now(), stored + 1)).toISOString(),
          },
        },
      };
    });
    persistQueue(get);
  },

  dismissSuperseded: (questionCode) => {
    set((s) => ({ supersededByCode: withoutKey(s.supersededByCode, questionCode) }));
  },
}));

function withoutKey<T>(rec: Record<string, T>, key: string): Record<string, T> {
  if (!(key in rec)) return rec;
  const { [key]: _drop, ...rest } = rec;
  return rest;
}

function setStore(patch: Partial<FormFillState>): void {
  useFormFillStore.setState(patch);
}

function scheduleFlush(get: () => FormFillState, delayMs?: number): void {
  scheduleFlushWith(get, setStore, delayMs);
}

export function selectValue(state: FormFillState, questionCode: string): unknown {
  if (state.pending[questionCode]) return state.pending[questionCode].value;
  return state.responsesByCode[questionCode]?.value ?? null;
}

export function selectNote(state: FormFillState, questionCode: string): string {
  if (state.pending[questionCode]?.consultantNote !== undefined) {
    return state.pending[questionCode].consultantNote ?? "";
  }
  return state.responsesByCode[questionCode]?.consultantNote ?? "";
}

/**
 * Bölüm bazlı progress — plan §5.3 7/9 formatı (M3-S4 ledger devri).
 * Bir yanıt "var" sayılır: value != null && (string için len>0, array için len>0, obj için key>0).
 */
export function selectSectionProgress(
  state: FormFillState,
  sectionId: string,
): { answered: number; requiredTotal: number; requiredAnswered: number; total: number } {
  if (!state.snapshot) return { answered: 0, requiredTotal: 0, requiredAnswered: 0, total: 0 };
  const sectionQuestions = state.snapshot.snapshot.questions.filter(
    (q) => q.sectionId === sectionId && q.enabled,
  );
  let answered = 0;
  let requiredAnswered = 0;
  let requiredTotal = 0;
  for (const q of sectionQuestions) {
    if (q.required) requiredTotal += 1;
    // Ö7 — sunucunun reddettiği (tutulan) değer kayıtlı sayılmaz: sayaç, eksik paneli gibi
    // sunucu durumunu yansıtır; aksi halde kenar çubuğu "dolu", panel "eksik" derdi.
    const v =
      state.errorsByCode[q.code] !== undefined && state.pending[q.code] !== undefined
        ? (state.responsesByCode[q.code]?.value ?? null)
        : selectValue(state, q.code);
    if (isAnswerComplete(v, q)) {
      answered += 1;
      if (q.required) requiredAnswered += 1;
    }
  }
  return { answered, requiredTotal, requiredAnswered, total: sectionQuestions.length };
}
